import { useState } from "react";
import { Loader2, Copy, Check, Save } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/context/LocaleContext";

const tr = (t, v) => (typeof v === "string" ? t(v) : v);

export function PageHeader({ overline, title, subtitle, action }) {
  const { t } = useLocale();
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        {overline && (
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3" data-testid="page-overline">
            {tr(t, overline)}
          </p>
        )}
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tighter text-foreground">{tr(t, title)}</h1>
        {subtitle && <p className="text-muted-foreground mt-2 max-w-2xl leading-relaxed">{tr(t, subtitle)}</p>}
      </div>
      {action}
    </div>
  );
}

export function Spinner({ label }) {
  const { t } = useLocale();
  return (
    <div className="flex items-center gap-3 text-muted-foreground py-16 justify-center" data-testid="loading-spinner">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span className="text-sm">{label ? tr(t, label) : t("Arbejder...")}</span>
    </div>
  );
}

export function CopyButton({ text, testid = "copy-button", label = "Kopiér" }) {
  const { t } = useLocale();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await navigator.clipboard.writeText(typeof text === "string" ? text : JSON.stringify(text, null, 2));
    setCopied(true);
    toast.success(t("Kopieret til udklipsholder"));
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <Button variant="secondary" size="sm" onClick={onCopy} data-testid={testid} className="gap-2">
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {label ? tr(t, label) : ""}
    </Button>
  );
}

export function SaveButton({ onSave, testid = "save-button", saved = false }) {
  const { t } = useLocale();
  return (
    <Button variant="outline" size="sm" onClick={onSave} data-testid={testid} className="gap-2" disabled={saved}>
      <Save className="h-3.5 w-3.5" />
      {saved ? t("Gemt") : t("Gem")}
    </Button>
  );
}

export function Surface({ children, className = "", testid, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: "easeOut" }}
      data-testid={testid}
      className={`rounded-xl border border-border bg-card p-5 sm:p-6 transition-colors duration-200 hover:border-muted-foreground/40 ${className}`}
    >
      {children}
    </motion.div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, testid }) {
  const { t } = useLocale();
  return (
    <div className="flex flex-col items-center justify-center text-center py-20 border border-dashed border-border rounded-xl" data-testid={testid}>
      {Icon && <Icon className="h-8 w-8 text-muted-foreground mb-4" strokeWidth={1.5} />}
      <p className="font-display text-lg text-foreground">{tr(t, title)}</p>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{tr(t, subtitle)}</p>}
    </div>
  );
}
